import { useCallback, useEffect, useState } from 'react'
import { Icon } from '../../../components/icons/Icon'
import { InlineAlert } from '../../../components/InlineAlert'
import { StatusPill, type StatusPillVariant } from '../../../components/StatusPill'
import { ErrorState } from '../../../design-system/list-view/states/ErrorState'
import { listCartridgeDrafts } from '../../../api/aiCartridgeDraft'
import { ApiError } from '../../../api/problem'
import { AiDraftRequestDialog } from './AiDraftRequestDialog'
import { AiDraftProposalCard } from './AiDraftProposalCard'
import type { AiCartridgeDraftProposalView, AiDraftKind } from '../../../types/aiCartridgeDraft'

export interface AiDraftTabProps {
  programId: string
}

const DRAFT_KIND_LABEL: Record<AiDraftKind, string> = {
  rule_set: 'RuleSet',
  parameter: 'Parameter',
  eligibility_model: 'EligibilityModel',
}

function statusVariant(status: AiCartridgeDraftProposalView['status']): StatusPillVariant {
  if (status === 'accepted') return 'success'
  if (status === 'rejected') return 'danger'
  return 'info'
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

/**
 * The AI-draft surface (IB-287, FR-M7.5, IB-211 Story 4) — one net-new tab in
 * `ProgramWorkspaceScreen`, beside RuleSets/Parameters, Eligibility, Calc graph. Lists prior
 * `CartridgeDraftProposal`s for this Program (`GET /cartridge-drafts?program_id=`), the "Request AI
 * draft" CTA, and the review card for a selected/just-generated proposal.
 *
 * <b>Capability-gate fallback (design doc §2.3/§7 dependency #2 — resolved at build).</b> The design
 * called for a courtesy `getUiConfig()` hide keyed on a `cartridge.ai-draft` component key IF IB-286
 * registered one. Re-verified against the merged `SecurityMatrixSeeder`/`VisibilityCompiler`
 * (`grep -rn "cartridge.ai-draft"` — zero hits) and `DemoDataSeeder` (`ai_engine:X`/`:A` seeded onto
 * the PlatformAdmin profile only, no new granular key): **no such key exists**. Per the design's own
 * disclosed fallback, this tab therefore renders its CTA unconditionally (the same posture the
 * surrounding Studio route already takes — "every mutation is fail-closed guarded server-side to the
 * platform tenant, no separate client-side role gate needed", `AppRouter.tsx`'s IB-210 comment) — the
 * real boundary is `CapabilityAiGuard`'s server-side `ai_engine:X`/`:A` 403, surfaced inline via
 * `friendlyGenerateError`/`friendlyAiCartridgeDraftError` exactly like every other failure path here,
 * never silently swallowed. Nothing here reads as "enforced end-to-end" client-side.
 *
 * Because the merged engine runs SYNCHRONOUSLY (`POST /cartridge-drafts` returns the fully-composed
 * proposal in one response — see `types/aiCartridgeDraft.ts`'s `[IB-286-verified]` note), there is no
 * `running` state to poll: the review card renders the instant the request dialog's promise resolves,
 * which already satisfies AC2's "no manual check-status step" without a polling loop.
 */
export function AiDraftTab({ programId }: AiDraftTabProps) {
  const [drafts, setDrafts] = useState<AiCartridgeDraftProposalView[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [requestOpen, setRequestOpen] = useState(false)

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoadError(null)
      listCartridgeDrafts(programId, signal)
        .then((page) => {
          if (signal?.aborted) return
          setDrafts(page.items)
          setSelectedId((prev) => prev ?? page.items[0]?.id ?? null)
        })
        .catch((err) => {
          if (signal?.aborted) return
          setDrafts(null)
          setLoadError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load AI drafts for this Program.')
        })
    },
    [programId],
  )

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  function handleGenerated(proposal: AiCartridgeDraftProposalView) {
    setDrafts((prev) => [proposal, ...(prev ?? [])])
    setSelectedId(proposal.id)
    setRequestOpen(false)
  }

  function handleDecided(updated: AiCartridgeDraftProposalView) {
    setDrafts((prev) => (prev ?? []).map((d) => (d.id === updated.id ? updated : d)))
  }

  if (loadError) {
    return <ErrorState message={loadError} onRetry={() => load()} />
  }

  if (drafts === null) {
    return <div style={{ padding: 'var(--iw-space-5)', color: 'var(--iw-text-muted)' }}>Loading…</div>
  }

  const selected = drafts.find((d) => d.id === selectedId) ?? null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
        <span style={{ color: 'var(--iw-accent)' }}>
          <Icon name="sparkles" size={18} />
        </span>
        <h2 style={{ margin: 0, fontSize: 'var(--iw-size-md)' }}>AI-assisted drafting</h2>
        <span style={{ flex: 1 }} />
        <button type="button" className="iw-btn iw-btn--primary iw-btn--sm" onClick={() => setRequestOpen(true)}>
          <Icon name="sparkles" size={14} /> Request AI draft
        </button>
      </div>

      <InlineAlert severity="info">
        AI-assisted — every draft here is advisory only. Accepting one creates a plain{' '}
        <strong>draft</strong> RuleSet/Parameter/EligibilityModel row that still must travel the full
        review → approve → publish path behind a distinct approver (maker ≠ checker); nothing on this
        tab ever publishes canon directly.
      </InlineAlert>

      {drafts.length === 0 ? (
        <div style={{ padding: 'var(--iw-space-5)', textAlign: 'center', color: 'var(--iw-text-muted)' }}>
          No AI drafts requested yet for this Program.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)' }}>
          {drafts.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => setSelectedId(d.id)}
              aria-current={d.id === selectedId}
              className="iw-btn"
              style={{
                justifyContent: 'flex-start',
                gap: 'var(--iw-space-2)',
                background: d.id === selectedId ? 'var(--iw-accent-subtle)' : undefined,
              }}
            >
              <span>{DRAFT_KIND_LABEL[d.draftKind]}</span>
              <StatusPill variant={statusVariant(d.status)} label={d.status} />
              <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>{formatDate(d.createdAt)}</span>
            </button>
          ))}
        </div>
      )}

      {selected ? (
        <AiDraftProposalCard programId={programId} proposal={selected} onAccepted={handleDecided} onRejected={handleDecided} />
      ) : null}

      {requestOpen ? (
        <AiDraftRequestDialog programId={programId} onClose={() => setRequestOpen(false)} onGenerated={handleGenerated} />
      ) : null}
    </div>
  )
}
